import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Dialog,
  Field,
  Icon,
  Input,
  Select,
  useErrorDialog,
  useToast,
} from '@dayon/design-system';
import { formatRate, percentToBasisPoints } from '@dayon/money';
import { api, ApiRequestError } from '../../lib/api.ts';
import type { AgentRow, PlanRow } from './types.ts';
import { PLAN_KIND_LABEL, bandSentence, entryRate, planFitsTier } from './vocabulary.tsx';

interface Brand {
  id: string;
  name: string;
}

interface Created {
  ref: string;
  name: string;
  status: string;
  planRef: string | null;
  planName: string | null;
  commissionRateBp: number;
}

/**
 * Create an agent.
 *
 * The form's fields were already the shape the write would take — that is why
 * they were rendered before there was anything to send them to. Three things
 * changed when the route landed:
 *
 *   · the button submits;
 *   · a BRAND is asked for. It was not, because nothing was written; a new
 *     agent belongs to exactly one brand, and defaulting silently to whatever
 *     the header happened to be scoped to would create agents in a brand
 *     nobody chose;
 *   · a PLAN or a rate is asked for. `agent.commission_rate_bp` is NOT NULL, so
 *     there is no such thing as an agent with no rate. Choosing a plan fills it
 *     from the plan's entry band, which is why the two fields are exclusive:
 *     two sources for one number is how they drift apart.
 *
 * The agent lands in PENDING_KYB, whatever this form says. There is no field
 * for status and the API does not accept one — an agent that could be created
 * ACTIVE is an agent that never passed KYB.
 */
export function NewAgentDialog({
  open,
  onClose,
  masters,
  brands,
  brandId,
  plans,
  loadFailed,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  /** The real master agents in the operator's brand scope. */
  masters: AgentRow[];
  brands: Brand[];
  /** The header's brand scope, or null for "all brands". */
  brandId: string | null;
  plans: PlanRow[];
  loadFailed: boolean;
  onCreated: (created: Created) => void;
}) {
  const { showError } = useErrorDialog();
  const { toast } = useToast();

  const [name, setName] = useState('');
  const [tier, setTier] = useState<'MASTER' | 'SUB_AGENT'>('SUB_AGENT');
  const [parent, setParent] = useState('');
  const [area, setArea] = useState('');
  /*
   * Empty when the header is scoped to "all brands".
   *
   * Defaulting to the first brand in the list narrowed the parent select to
   * that brand's masters without anybody choosing it — so an operator working
   * across brands opened the form and found two of their three masters simply
   * missing, with nothing on screen saying why. Empty means "not yet chosen":
   * every master in scope is offered, and picking one SETS the brand, because
   * a sub-agent must sit under a master in its own brand anyway.
   */
  const [brand, setBrand] = useState(brandId ?? '');
  const [planId, setPlanId] = useState('');
  const [ratePercent, setRatePercent] = useState('');
  const [fields, setFields] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  // Re-open on the header's brand rather than on whatever was left behind by
  // the last attempt: the previous brand is the wrong default and is the one
  // mistake this form cannot show you.
  useEffect(() => {
    if (!open) return;
    setBrand(brandId ?? '');
    setFields({});
  }, [open, brandId]);

  /** Masters the new sub-agent may sit under: the chosen brand's, and no other.
      With no brand chosen yet, every master in the operator's scope. */
  const eligibleMasters = useMemo(
    () => masters.filter((m) => !brand || brands.find((b) => b.id === brand)?.name === m.brand),
    [masters, brand, brands],
  );

  /** The brand a chosen master belongs to, so picking a parent settles it. */
  const brandOfMaster = (masterId: string): string =>
    brands.find((b) => b.name === masters.find((m) => m.id === masterId)?.brand)?.id ?? '';

  /** Plans offered on the chosen brand, to this tier, and not withdrawn. */
  const eligiblePlans = useMemo(
    () =>
      plans.filter(
        (p) => p.status === 'ACTIVE' && p.brand_id === brand && planFitsTier(p, tier),
      ),
    [plans, brand, tier],
  );

  const chosenPlan = eligiblePlans.find((p) => p.id === planId) ?? null;
  const planRate = chosenPlan ? entryRate(chosenPlan.bands) : null;

  const reset = () => {
    setName('');
    setTier('SUB_AGENT');
    setParent('');
    setArea('');
    setPlanId('');
    setRatePercent('');
    setFields({});
  };

  const close = () => {
    reset();
    onClose();
  };

  /** What the form can refuse without asking the server. */
  const localProblems = (): Record<string, string> => {
    const problems: Record<string, string> = {};
    if (name.trim().length < 2) problems['name'] = 'Give the agent a name.';
    if (!brand) problems['brandId'] = 'Choose the brand this agent belongs to.';
    if (tier === 'SUB_AGENT' && !parent) problems['parentId'] = 'A sub-agent needs a master.';
    if (!planId) {
      if (ratePercent.trim() === '') {
        problems['commissionRateBp'] = 'Choose a plan, or set a rate for this agent.';
      } else if (!/^\d+(\.\d{1,2})?$/.test(ratePercent.trim())) {
        problems['commissionRateBp'] = 'A rate is a percentage, e.g. 4 or 4.5.';
      } else if (percentToBasisPoints(Number(ratePercent)) > 10_000) {
        problems['commissionRateBp'] = 'A rate cannot exceed 100%.';
      }
    }
    return problems;
  };

  const submit = async () => {
    const problems = localProblems();
    if (Object.keys(problems).length > 0) {
      setFields(problems);
      return;
    }
    setSaving(true);
    setFields({});
    try {
      const created = await api.post<Created>('/api/admin/agents', {
        name: name.trim(),
        brandId: brand,
        tier,
        parentId: tier === 'SUB_AGENT' ? parent : null,
        ...(area.trim() ? { area: area.trim() } : {}),
        ...(planId
          ? { planId }
          : { commissionRateBp: percentToBasisPoints(Number(ratePercent)) }),
      });
      toast(
        `${created.ref} created · pending until a KYB outcome is recorded${
          created.planName ? `, on ${created.planName}` : ''
        }.`,
      );
      reset();
      onCreated(created);
    } catch (error: unknown) {
      // Field-level problems stay inline. Everything else is an agent that was
      // NOT created, and that opens the modal.
      if (error instanceof ApiRequestError && error.fields) {
        setFields(error.fields);
        return;
      }
      showError({
        title: 'The agent was not created',
        message:
          error instanceof ApiRequestError
            ? error.message
            : 'Nothing was saved. Try again shortly.',
        ...(error instanceof ApiRequestError ? { code: error.code } : {}),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      title="New agent"
      wide
      actions={
        <>
          <Button onClick={close}>Cancel</Button>
          <Button variant="primary" disabled={saving} onClick={() => void submit()}>
            {saving ? 'Creating…' : 'Create agent'}
          </Button>
        </>
      }
    >
      <p className="text-muted" style={{ fontSize: '13px' }}>
        The agent lands in <strong>Pending</strong> and cannot recruit players until an operator
        records a KYB pass against it.
      </p>

      <div className="stack">
        <Field label="Agent name" error={fields['name']}>
          {({ id, describedBy }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ilocos Gaming Group"
            />
          )}
        </Field>

        <Field
          label="Brand"
          error={fields['brandId']}
          hint="An agent belongs to one brand, and its master must belong to the same one."
        >
          {({ id, describedBy }) => (
            <Select
              id={id}
              aria-describedby={describedBy}
              value={brand}
              onChange={(e) => {
                setBrand(e.target.value);
                // A parent and a plan chosen under one brand do not exist under
                // another, and submitting them would be refused by the server
                // for a reason the form could have prevented.
                setParent('');
                setPlanId('');
              }}
            >
              <option value="">Select a brand…</option>
              {brands.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </Select>
          )}
        </Field>

        {/* Tier is asked BEFORE parent, unlike the design's order: the tier
            decides whether a parent is legal at all, and a select you fill in
            and then watch get disabled is a question that should not have
            been asked yet. */}
        <Field label="Tier">
          {({ id }) => (
            <Select
              id={id}
              value={tier}
              onChange={(e) => {
                setTier(e.target.value as 'MASTER' | 'SUB_AGENT');
                setParent('');
                setPlanId('');
              }}
            >
              <option value="MASTER">Master</option>
              <option value="SUB_AGENT">Sub-agent</option>
            </Select>
          )}
        </Field>

        <Field
          label="Parent"
          error={fields['parentId']}
          hint={
            tier === 'MASTER'
              ? 'A master sits at the top of the tree and has no parent. Not a preference — the platform will not store one any other way.'
              : 'Only masters can hold a downline; the hierarchy is three levels, so a sub-agent cannot parent another sub-agent.'
          }
        >
          {({ id, describedBy }) => (
            <Select
              id={id}
              aria-describedby={describedBy}
              disabled={tier === 'MASTER'}
              value={parent}
              onChange={(e) => {
                setParent(e.target.value);
                // A sub-agent belongs to its master's brand — the API refuses
                // any other pairing — so choosing the parent answers the brand
                // question rather than leaving it to be got wrong.
                if (e.target.value && !brand) setBrand(brandOfMaster(e.target.value));
              }}
            >
              <option value="">Select a master agent…</option>
              {eligibleMasters.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.ref} · {m.name}
                </option>
              ))}
            </Select>
          )}
        </Field>

        <Field label="Area" hint="Optional. The region this agent recruits in.">
          {({ id, describedBy }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              value={area}
              onChange={(e) => setArea(e.target.value)}
              placeholder="Ilocos"
            />
          )}
        </Field>

        <Field
          label="Commission plan"
          error={fields['planId']}
          hint={
            eligiblePlans.length === 0
              ? 'No active plan is offered to this tier on this brand. Set a rate below instead, or create a plan first.'
              : 'The agent starts on the plan’s entry band. Leave this unset to agree a rate by hand.'
          }
        >
          {({ id, describedBy }) => (
            <Select
              id={id}
              aria-describedby={describedBy}
              value={planId}
              onChange={(e) => setPlanId(e.target.value)}
            >
              <option value="">No plan — set a rate by hand</option>
              {eligiblePlans.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.ref} · {p.name} · {bandSentence(p.bands)}
                </option>
              ))}
            </Select>
          )}
        </Field>

        {chosenPlan ? (
          <div className="notice notice-quiet">
            <Icon name="percent" />
            <div>
              <strong>{chosenPlan.name}</strong> — {PLAN_KIND_LABEL[chosenPlan.kind]},{' '}
              {bandSentence(chosenPlan.bands)}. The agent carries{' '}
              <strong>{planRate === null ? 'no rate' : formatRate(planRate)}</strong> as its own
              rate, which is the plan&rsquo;s entry band; the bands above it are held on the plan.
            </div>
          </div>
        ) : (
          <Field
            label="Commission rate"
            error={fields['commissionRateBp']}
            hint="A percentage of net GGR. Stored in basis points, so 4.5 becomes 450."
          >
            {({ id, describedBy }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                inputMode="decimal"
                value={ratePercent}
                onChange={(e) => setRatePercent(e.target.value)}
                placeholder="4.0"
              />
            )}
          </Field>
        )}

        {loadFailed && (
          <div className="notice notice-quiet">
            <Icon name="warning-circle" />
            <div>
              The agent read failed, so no master is offered above — the list is unread, not empty.
              Reload before creating a sub-agent.
            </div>
          </div>
        )}
      </div>
    </Dialog>
  );
}
